import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Row,
  Col,
  Statistic,
  Button,
  Typography,
  Flex,
  Alert,
  Avatar,
  Tag,
  Card,
  Skeleton,
  Grid,
  Divider,
  theme,
} from "antd";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ClockCircleOutlined,
  ContainerOutlined,
  AppstoreOutlined,
  UserOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useCustom } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import {
  AdminDashboardDto,
  UsersCompletedOrdersReportDto,
  UserCompletedOrdersReportItemDto,
} from "../../models/dashboard";
import { GlassCard } from "../../shared/components";
import { getChartSeriesColors, usePageStyles } from "../../shared/styles";

const MEAN_TIME_GREEN_MAX = 115;
const MEAN_TIME_ORANGE_MAX = 130;

function getAccountInitials(name: string | null): string {
  if (!name || !name.trim()) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const styles = usePageStyles();
  const { token } = theme.useToken();
  const chartSeriesColors = useMemo(
    () => getChartSeriesColors(token),
    [token],
  );
  const teamAvatarColors = useMemo(
    () => [
      token.colorPrimary,
      token.colorSuccess,
      token.colorWarning,
      token.colorInfo,
    ],
    [token],
  );
  const topAccountAvatarColors = useMemo(
    () => [
      chartSeriesColors[0],
      chartSeriesColors[1],
      chartSeriesColors[2],
      chartSeriesColors[3],
      chartSeriesColors[4],
    ],
    [chartSeriesColors],
  );
  const breakpoint = Grid.useBreakpoint();
  const isMobile = typeof breakpoint.lg === "undefined" ? false : !breakpoint.lg;

  // On mobile: render children flat with divider; on desktop: wrap in GlassCard
  const Section: React.FC<{ children: React.ReactNode; className?: string }> = ({
    children,
    className,
  }) =>
    isMobile ? (
      <>
        <div className={`py-3 ${className ?? ""}`}>{children}</div>
        <Divider className="mb-0 mt-2"/>
      </>
    ) : (
      <GlassCard className={className}>{children}</GlassCard>
    );

  // Last 30 days for team performance report (ISO 8601)
  const { startDate: reportStartDate, endDate: reportEndDate } = useMemo(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end);
    start.setDate(start.getDate() - 30);
    start.setHours(0, 0, 0, 0);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }, []);

  // Fetch dashboard data from the API
  const {
    data: dashboardData,
    isLoading,
    error,
  } = useCustom<AdminDashboardDto>({
    url: "dashboard/admin",
    method: "get",
  });

  const { data: teamReportData, isLoading: teamReportLoading } =
    useCustom<UsersCompletedOrdersReportDto>({
      url: "dashboard/admin/users-completed-orders-report",
      method: "get",
      config: {
        query: { startDate: reportStartDate, endDate: reportEndDate },
      },
    });

  useEffect(() => {
    console.log("Dashboard mounted");
    console.log("Dashboard data:", dashboardData);
  }, [dashboardData]);

  const handleDateNavigation = (direction: "prev" | "next") => {
    const newDate = new Date(currentDate);
    if (direction === "prev") {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };

  // Transform order count by product for pie chart
  const productChartData =
    dashboardData?.data?.orderCountByProduct
      ?.filter((item) => item.productName && item.orderCount !== null)
      ?.map((item) => ({
        name: item.productName!,
        value: item.orderCount!,
      })) || [];

  // Debug logging
  console.log(
    "Raw orderCountByProduct:",
    dashboardData?.data?.orderCountByProduct,
  );
  console.log("Filtered productChartData:", productChartData);

  // If no valid data, create default chart data from product names
  const defaultProductChartData =
    dashboardData?.data?.orderCountByProduct
      ?.filter((item) => item.productName)
      ?.map((item, index) => ({
        name: item.productName!,
        value: 1, // Default value of 1 for each product
      })) || [];

  const finalProductChartData =
    productChartData.length > 0 ? productChartData : defaultProductChartData;
  const totalOrders = finalProductChartData.reduce((s, d) => s + d.value, 0);

  // Transform top accounts for bar chart
  const topAccountsChartData =
    dashboardData?.data?.topAccounts
      ?.filter((item) => item.accountName && item.orderCount !== null)
      ?.slice(0, 5)
      ?.map((account) => ({
        name: account.accountName!,
        orders: account.orderCount!,
      })) || [];

  // Debug logging for top accounts
  console.log("Raw topAccounts:", dashboardData?.data?.topAccounts);
  console.log("Filtered topAccountsChartData:", topAccountsChartData);

  // If no valid data, create default chart data from account names
  const defaultTopAccountsChartData =
    dashboardData?.data?.topAccounts
      ?.filter((item) => item.accountName)
      ?.slice(0, 5)
      ?.map((item, index) => ({
        name: item.accountName!,
        orders: 1, // Default value of 1 for each account
      })) || [];

  const finalTopAccountsChartData =
    topAccountsChartData.length > 0
      ? topAccountsChartData
      : defaultTopAccountsChartData;

  const teamUsers = teamReportData?.data?.users ?? [];

  const getDisplayName = (u: UserCompletedOrdersReportItemDto) => {
    const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
    return name || u.email || t("dashboard.teamPerformance.unknownUser");
  };
  const getInitials = (u: UserCompletedOrdersReportItemDto) => {
    if (u.firstName && u.lastName)
      return `${u.firstName[0]}${u.lastName[0]}`.toUpperCase();
    if (u.firstName) return u.firstName.slice(0, 2).toUpperCase();
    if (u.email) return u.email.slice(0, 2).toUpperCase();
    return "?";
  };
  const getMeanTimeColor = useCallback(
    (minutes: number) => {
      if (minutes < MEAN_TIME_GREEN_MAX) return token.colorSuccess;
      if (minutes <= MEAN_TIME_ORANGE_MAX) return token.colorWarning;
      return token.colorError;
    },
    [token],
  );

  // Transform daily order counts for area chart
  const dailyOrderChartData =
    dashboardData?.data?.dailyOrderCounts?.map((item) => ({
      date: new Date(item.date).toLocaleDateString("fr-FR", {
        month: "short",
        day: "numeric",
      }),
      orders: item.orderCount,
    })) || [];

  const totalTransactionsAmountCents =
    dashboardData?.data?.totalTransactionsAmount ?? 0;
  const hasTransactionsAmount = totalTransactionsAmountCents > 0;
  const totalTransactionsAmount = totalTransactionsAmountCents / 100;

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
          {/* KPI cards skeleton */}
          <Row gutter={[16, 16]} className="mb-6">
            {[1, 2, 3, 4].map((i) => (
              <Col xs={24} sm={12} lg={6} xl={4} key={i}>
                <GlassCard>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </GlassCard>
              </Col>
            ))}
          </Row>
          {/* Charts row skeleton */}
          <Row gutter={[16, 16]} className="mb-6">
            <Col xs={24} lg={12}>
              <GlassCard>
                <Skeleton active title paragraph={{ rows: 6 }} />
              </GlassCard>
            </Col>
            <Col xs={24} lg={12}>
              <GlassCard>
                <Skeleton active title paragraph={{ rows: 6 }} />
              </GlassCard>
            </Col>
          </Row>
          {/* Team performance skeleton */}
          <Row gutter={[16, 16]} className="mb-6">
            <Col span={24}>
              <GlassCard>
                <Skeleton active title paragraph={{ rows: 2 }} />
                <Row gutter={[16, 16]} className="mt-4">
                  {[1, 2, 3, 4].map((i) => (
                    <Col xs={24} sm={12} md={6} key={i}>
                      <Skeleton active avatar paragraph={{ rows: 2 }} />
                    </Col>
                  ))}
                </Row>
              </GlassCard>
            </Col>
          </Row>
          {/* Daily orders chart skeleton */}
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <GlassCard>
                <Skeleton active title paragraph={{ rows: 2 }} />
                <div className="mt-4 h-[350px]">
                  <Skeleton active paragraph={{ rows: 8 }} />
                </div>
              </GlassCard>
            </Col>
          </Row>
      </div>
    );
  }

  if (error) {
    return (
        <Alert
          message={t("dashboard.error.title")}
          description={t("dashboard.error.description")}
          type="error"
          showIcon
        />
    );
  }

  return (
    <div className={isMobile ? "px-4 pt-4 pb-8" : "p-6 max-w-7xl mx-auto"}>
        <Typography.Title
          level={3}
          className="!m-0 !mb-8"
          style={{ color: styles.container.color }}
          hidden={true}
        >
          {t("dashboard.title")}
        </Typography.Title>

        {/* KPI Cards */}
        <Row gutter={isMobile ? [8, 8] : [16, 16]} className="mb-4">
          <Col xs={24} sm={12} lg={4} xl={3} hidden={true}>
            <Section>
              <Statistic
                title={t("dashboard.kpiCards.averageCompletionTime")}
                value={Math.round(
                  dashboardData?.data?.averageOrderLeadTime || 0,
                )}
                suffix="jours"
                prefix={
                  <span className="mr-3">
                    <ClockCircleOutlined
                      style={{ color: styles.primaryColor }}
                    />
                  </span>
                }
                valueStyle={{ color: styles.primaryColor }}
              />
            </Section>
          </Col>
          <Col xs={12} sm={12} lg={8} xl={6}>
            <Section>
              <Statistic
                title={t("dashboard.kpiCards.totalOrders")}
                value={dashboardData?.data?.overallOrderCount || 0}
                prefix={
                  <span className="mr-3">
                    <ContainerOutlined style={{ color: token.colorSuccess }} />
                  </span>
                }
                valueStyle={{ color: token.colorSuccess }}
                groupSeparator=" "
              />
            </Section>
          </Col>
          {hasTransactionsAmount && (
            <Col xs={12} sm={12} lg={8} xl={6}>
              <Section>
                <Statistic
                  title={t("dashboard.kpiCards.totalTransactionsAmount")}
                  value={totalTransactionsAmount}
                  precision={0}
                  suffix="€"
                  groupSeparator=" "
                  valueStyle={{ color: token.colorInfo }}
                />
              </Section>
            </Col>
          )}
          <Col xs={12} sm={12} lg={8} xl={6}>
            <Section>
              <Statistic
                title={t("dashboard.kpiCards.totalAccounts")}
                value={dashboardData?.data?.overallAccountCount || 0}
                prefix={
                  <span className="mr-3">
                    <UserOutlined style={{ color: token.colorPrimary }} />
                  </span>
                }
                valueStyle={{ color: token.colorPrimary }}
                groupSeparator=" "
              />
            </Section>
          </Col>
          <Col xs={12} sm={12} lg={8} xl={6}>
            <Section>
              <Statistic
                title={t("dashboard.kpiCards.totalProducts")}
                value={dashboardData?.data?.overallProductCount || 0}
                prefix={
                  <span className="mr-3">
                    <AppstoreOutlined style={{ color: token.colorWarning }} />
                  </span>
                }
                valueStyle={{ color: token.colorWarning }}
                groupSeparator=" "
              />
            </Section>
          </Col>
        </Row>

        {/* Charts Row 1 */}
        <Row gutter={isMobile ? [8, 8] : [16, 16]} className="mb-4">
          <Col xs={24} lg={12}>
            <Section>
              <div className="flex flex-col gap-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Typography.Title
                      level={4}
                      className="!mb-1"
                      style={{ color: styles.container.color }}
                    >
                      {t("dashboard.charts.ordersPerProduct")}
                    </Typography.Title>
                    <Typography.Text
                      type="secondary"
                      className="!text-[13px]"
                      style={{ color: styles.emptyText?.color }}
                    >
                      {t("dashboard.charts.ordersPerProductSubtitle")}
                    </Typography.Text>
                  </div>
                  <Button
                    type="default"
                    size="small"
                    onClick={() => navigate("/products")}
                    className="rounded-lg border border-gray-500/30 bg-transparent text-inherit hover:!border-gray-400/50 hover:!bg-white/5"
                  >
                    {t("buttons.showAll")}
                  </Button>
                </div>
                {finalProductChartData.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {finalProductChartData.map((item, index) => {
                      const percent = totalOrders
                        ? (item.value / totalOrders) * 100
                        : 0;
                      return (
                        <div
                          key={item.name ?? index}
                          className="flex flex-col gap-2 rounded-lg px-4 py-3 w-full"
                          style={{ backgroundColor: styles.rowBackground }}
                        >
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <Typography.Text
                                strong
                                className="truncate"
                                style={{ color: styles.container.color }}
                              >
                                {item.name}
                              </Typography.Text>
                              <Tag
                                className="shrink-0"
                                style={{
                                  color: styles.emptyText?.color,
                                  backgroundColor: "rgba(255,255,255,0.08)",
                                  border: "none",
                                }}
                              >
                                {item.value}{" "}
                                {t("dashboard.topAccounts.demandes")}
                              </Tag>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Typography.Text
                                strong
                                style={{ color: styles.container.color }}
                              >
                                {item.value}
                              </Typography.Text>
                              <Typography.Text
                                type="secondary"
                                className="!text-[13px]"
                                style={{ color: styles.emptyText?.color }}
                              >
                                {percent.toFixed(0)}%
                              </Typography.Text>
                            </div>
                          </div>
                          <div
                            className="w-full h-2 rounded-full overflow-hidden"
                            style={{
                              backgroundColor: "rgba(255,255,255,0.08)",
                            }}
                          >
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${percent}%`,
                                backgroundColor:
                                  chartSeriesColors[
                                    index % chartSeriesColors.length
                                  ],
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-center py-16"
                    style={{ color: styles.emptyText?.color }}
                  >
                    {t("dashboard.noData")}
                  </div>
                )}
              </div>
            </Section>
          </Col>
          <Col xs={24} lg={12}>
            <Section>
              <div className="flex flex-col gap-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Typography.Title
                      level={4}
                      className="!mb-1"
                      style={{ color: styles.container.color }}
                    >
                      {t("dashboard.charts.topAccounts")}
                    </Typography.Title>
                    <Typography.Text
                      type="secondary"
                      className="!text-[13px]"
                      style={{ color: styles.emptyText?.color }}
                    >
                      {t("dashboard.charts.topAccountsSubtitle")}
                    </Typography.Text>
                  </div>
                  <Button
                    type="default"
                    size="small"
                    onClick={() => navigate("/accounts")}
                    className="rounded-lg border border-gray-500/30 bg-transparent text-inherit hover:!border-gray-400/50 hover:!bg-white/5"
                  >
                    {t("buttons.showAll")}
                  </Button>
                </div>
                {finalTopAccountsChartData.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {finalTopAccountsChartData.map((account, index) => (
                      <div
                        key={account.name ?? index}
                        className="flex items-center gap-4 rounded-lg px-4 py-3 w-full"
                        style={{ backgroundColor: styles.rowBackground }}
                      >
                        <span
                          className="text-lg font-bold tabular-nums shrink-0 w-6"
                          style={{ color: styles.container.color }}
                        >
                          {index + 1}
                        </span>
                        <Avatar
                          size={40}
                          className="shrink-0 !text-sm"
                          style={{
                            backgroundColor:
                              topAccountAvatarColors[
                                index % topAccountAvatarColors.length
                              ],
                          }}
                        >
                          {getAccountInitials(account.name)}
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <Typography.Text
                            strong
                            className="block truncate"
                            style={{ color: styles.container.color }}
                          >
                            {account.name}
                          </Typography.Text>
                        </div>
                        <div className="flex flex-col items-end shrink-0">
                          <div className="flex items-center gap-1.5">
                            <Typography.Text
                              strong
                              className="!text-lg"
                              style={{ color: styles.container.color }}
                            >
                              {account.orders}
                            </Typography.Text>
                            <MinusOutlined
                              className="!text-xs"
                              style={{ color: "rgba(255,255,255,0.45)" }}
                            />
                          </div>
                          <Typography.Text
                            type="secondary"
                            className="!text-xs"
                            style={{ color: styles.emptyText?.color }}
                          >
                            {t("dashboard.topAccounts.demandes")}
                          </Typography.Text>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-center py-16"
                    style={{ color: styles.emptyText?.color }}
                  >
                    {t("dashboard.noData")}
                  </div>
                )}
              </div>
            </Section>
          </Col>
        </Row>

        {/* Team Performance */}
        <Row gutter={isMobile ? [8, 8] : [16, 16]} className="mb-4">
          <Col span={24}>
            <Section>
              <div className="mb-4">
                <Typography.Title
                  level={4}
                  className="!m-0 !mb-1"
                  style={{ color: styles.container.color }}
                >
                  {t("dashboard.teamPerformance.title")}
                </Typography.Title>
                <Typography.Text
                  type="secondary"
                  style={{ color: styles.emptyText?.color }}
                >
                  {t("dashboard.teamPerformance.subtitle")}
                </Typography.Text>
              </div>
              {teamReportLoading ? (
                <Row gutter={[16, 16]}>
                  {[1, 2, 3, 4].map((i) => (
                    <Col xs={24} sm={12} md={6} lg={5} xl={4} key={i}>
                      <GlassCard>
                        <Skeleton active avatar paragraph={{ rows: 3 }} />
                      </GlassCard>
                    </Col>
                  ))}
                </Row>
              ) : teamUsers.length === 0 ? (
                <div
                  className="p-6 text-center"
                  style={{ color: styles.emptyText?.color }}
                >
                  {t("dashboard.teamPerformance.noData")}
                </div>
              ) : (
                <Row gutter={[16, 16]}>
                  {teamUsers.map(
                    (user: UserCompletedOrdersReportItemDto, index: number) => {
                      const meanMinutes = Math.round(
                        user.meanTimeToCompleteSeconds / 60,
                      );
                      const meanTimeColor = getMeanTimeColor(meanMinutes);
                      const avatarColor =
                        teamAvatarColors[index % teamAvatarColors.length];
                      return (
                        <Col
                          xs={24}
                          sm={12}
                          md={6}
                          lg={5}
                          xl={4}
                          key={user.userId}
                        >
                          <Section>
                            <div className="flex flex-col items-center text-center">
                              <div className="relative inline-block mb-3">
                                <Avatar
                                  size={56}
                                  className="!text-xl"
                                  style={{ backgroundColor: avatarColor }}
                                >
                                  {getInitials(user)}
                                </Avatar>
                                <span
                                  className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white"
                                  style={{ backgroundColor: token.colorSuccess }}
                                />
                              </div>
                              <Typography.Text
                                strong
                                className="!mb-4 !block"
                                style={{ color: styles.container.color }}
                              >
                                {getDisplayName(user)}
                              </Typography.Text>
                              <div className="w-full space-y-2 text-left">
                                <Card
                                  size="small"
                                  className="rounded-lg border-0 bg-gray-500/10"
                                >
                                  <div className="flex justify-between items-baseline">
                                    <span
                                      style={{ color: styles.emptyText?.color }}
                                    >
                                      {t("dashboard.teamPerformance.completed")}
                                    </span>
                                    <span
                                      className="font-semibold"
                                      style={{ color: styles.container.color }}
                                    >
                                      {user.completedOrdersCount}
                                    </span>
                                  </div>
                                </Card>
                                <Card
                                  size="small"
                                  className="rounded-lg border-0 bg-gray-500/10"
                                >
                                  <div className="flex justify-between items-baseline">
                                    <span
                                      style={{ color: styles.emptyText?.color }}
                                    >
                                      {t("dashboard.teamPerformance.meanTime")}
                                    </span>
                                    <span
                                      className="font-semibold"
                                      style={{ color: meanTimeColor }}
                                    >
                                      {meanMinutes} min
                                    </span>
                                  </div>
                                </Card>
                              </div>
                            </div>
                          </Section>
                        </Col>
                      );
                    },
                  )}
                </Row>
              )}
            </Section>
          </Col>
        </Row>

        {/* Daily Orders Chart */}
        <Row gutter={isMobile ? [8, 8] : [16, 16]}>
          <Col span={24}>
            <Section>
              <div className="mb-4">
                <Typography.Title
                  level={4}
                  className="!m-0 !mb-1"
                  style={{ color: styles.container.color }}
                >
                  {t("dashboard.charts.ordersPerDay")}
                </Typography.Title>
                <Typography.Text
                  type="secondary"
                  style={{ color: styles.emptyText?.color }}
                >
                  {t("dashboard.charts.ordersPerDaySubtitle")}
                </Typography.Text>
              </div>
              <Flex
                gap="small"
                justify="space-between"
                className="!mb-6"
              >
                <div />
                <Flex gap="small" align="center">
                  <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => handleDateNavigation("prev")}
                    size="small"
                    style={styles.headerButton}
                  />
                  <span
                    className="min-w-[120px] text-center"
                    style={{ color: styles.container.color }}
                  >
                    {currentDate.toLocaleDateString("fr-FR", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                  <Button
                    icon={<ArrowRightOutlined />}
                    onClick={() => handleDateNavigation("next")}
                    size="small"
                    style={styles.headerButton}
                  />
                </Flex>
              </Flex>

              <ResponsiveContainer height={350} width="100%">
                <AreaChart
                  data={dailyOrderChartData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="orders"
                    stroke={styles.primaryColor}
                    fill={styles.primaryColor}
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Section>
          </Col>
        </Row>
    </div>
  );
};
